import React, { useCallback } from 'react';
import { Col, Form, Row, Tooltip } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import AppSelect from '@nxsys/components/AppSelect';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { PAYMENT_MODEL_TEMPLATES, PAYSLIP_TEMPLATES } from '@nxsys/utils';
import {
  getDemoTemplateLayout,
  getDemoCisTemplateLayout,
} from '@nxsys/constants/templateFiles';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import type { PayrollFinanceSectionProps } from './types';
import { useSelector } from 'react-redux';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';


const PayslipTemplatesSection: React.FC<PayrollFinanceSectionProps> = ({
  form,
  watchValues,
  visibility,
  hasPayeFTH,
}) => {
  const { messages } = useTypedIntl();
  // NX-5228: Ireland's umbrella models are PAYE, RCT (stored as CIS) and
  // Umbrella, each with a single template. Hybrid, PAYE Hybrid and PAYE FTH
  // do not exist there.
  const { payrollCountry } = useSelector((state: AppState) => state.appStore);
  const isIreland = payrollCountry === IRELAND;

  const handlePreview = useCallback(
    (templateValue: string | undefined, paymentModel: string) => {
      if (!templateValue) return;
      const url =
        paymentModel === 'CIS'
          ? getDemoCisTemplateLayout(templateValue)
          : getDemoTemplateLayout(templateValue, paymentModel);
      if (url) window.open(url, '_blank');
    },
    [],
  );

  if (!visibility.payslipTemplates) return null;

  const getSelectedValue = (fieldName: string) => {
    const val = (watchValues?.payslipTemplates as Record<string, any>)?.[
      fieldName
    ] as { value?: string } | null | undefined;
    return val?.value;
  };

  const renderPreviewButton = (fieldName: string, paymentModel: string) => {
    const selectedValue = getSelectedValue(fieldName);
    if (!selectedValue) return null;
    return (
      <Tooltip title={messages['common.preview'] ?? 'Preview'}>
        <EyeOutlined
          className="cursor-pointer text-lg text-gray-500 hover:text-primary mb-[35px]"
          onClick={() => handlePreview(selectedValue, paymentModel)}
        />
      </Tooltip>
    );
  };

  return (
    <div className="pt-6">
      <div className="mb-6 text-xl font-medium text-gray-800">
        {messages['common.payslipTemplates']}
      </div>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            label={messages['common.attachLogo']}
            name={['payslipTemplates', 'logoUrl']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppUploadFile
              urlFile={
                typeof watchValues?.payslipTemplates?.logoUrl === 'string'
                  ? watchValues?.payslipTemplates?.logoUrl
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldValue(['payslipTemplates', 'logoUrl'], file);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            label={messages['common.attachBanner']}
            name={['payslipTemplates', 'bannerUrl']}
            labelCol={{ span: 24 }}
            wrapperCol={{ span: 24 }}
          >
            <AppUploadFile
              urlFile={
                typeof watchValues?.payslipTemplates?.bannerUrl === 'string'
                  ? watchValues?.payslipTemplates?.bannerUrl
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldValue(['payslipTemplates', 'bannerUrl'], file);
              }}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Form.Item
                label={messages['common.paye']}
                name={['payslipTemplates', 'payePayslipTemplate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={
                    isIreland
                      ? [PAYSLIP_TEMPLATES.DEFAULT]
                      : PAYMENT_MODEL_TEMPLATES.PAYE
                  }
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </div>
            {renderPreviewButton('payePayslipTemplate', 'PAYE')}
          </div>
        </Col>
        <Col span={12}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Form.Item
                label={messages['common.umbrella']}
                name={['payslipTemplates', 'umbrellaPayslipTemplate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={
                    isIreland
                      ? [PAYSLIP_TEMPLATES.DEFAULT]
                      : PAYMENT_MODEL_TEMPLATES.UMBRELLA
                  }
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </div>
            {renderPreviewButton('umbrellaPayslipTemplate', 'UMBRELLA')}
          </div>
        </Col>
        {!isIreland && (
        <Col span={12}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Form.Item
                label={messages['common.umbrellaHybrid']}
                name={['payslipTemplates', 'hybridPayslipTemplate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={PAYMENT_MODEL_TEMPLATES.HYBRID}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </div>
            {renderPreviewButton('hybridPayslipTemplate', 'HYBRID')}
          </div>
        </Col>
        )}
        {!isIreland && (
        <Col span={12}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Form.Item
                label={messages['common.payeHybrid']}
                name={['payslipTemplates', 'hybridPayePayslipTemplate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={PAYMENT_MODEL_TEMPLATES.PAYE_HYBRID}
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </div>
            {renderPreviewButton('hybridPayePayslipTemplate', 'PAYE')}
          </div>
        </Col>
        )}
        <Col span={12}>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <Form.Item
                label={isIreland ? 'RCT' : messages['common.cis']}
                name={['payslipTemplates', 'cisPayslipTemplate']}
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
              >
                <AppSelect
                  options={
                    isIreland
                      ? [PAYSLIP_TEMPLATES.DEFAULT]
                      : PAYMENT_MODEL_TEMPLATES.CIS
                  }
                  placeholder={messages['form.select']}
                />
              </Form.Item>
            </div>
            {renderPreviewButton('cisPayslipTemplate', 'CIS')}
          </div>
        </Col>
        {!isIreland && (
        <Col span={12}>
          {hasPayeFTH && (
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Form.Item
                  label={messages['common.payeFTH']}
                  name={['payslipTemplates', 'payeETHPayslipTemplate']}
                  labelCol={{ span: 24 }}
                  wrapperCol={{ span: 24 }}
                >
                  <AppSelect
                    options={PAYMENT_MODEL_TEMPLATES.PAYE_FTH}
                    placeholder={messages['form.select']}
                  />
                </Form.Item>
              </div>
              {renderPreviewButton('payeETHPayslipTemplate', 'PAYE_FTH')}
            </div>
          )}
        </Col>
        )}
      </Row>
    </div>
  );
};

export default PayslipTemplatesSection;
